import { useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { apiClient, APIMessageResponse } from '@/shared/services/apiConfig';
import { TerminalType, UserTerminal } from '@/shared/types/userTerminalTypes';
import { PaginatedResponse } from '@/shared/types/responseTypes';

/** Infinite scroll fetch for user terminals load-more. */
export const useInfiniteUserTerminals = (regionCode: string | undefined) => {
  return useInfiniteQuery({
    queryKey: ['region-user-terminals-infinite', regionCode],
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const response = await apiClient.get<PaginatedResponse<UserTerminal>>(
        `/enterprise/v1/user-terminals`,
        { params: { regionCode, page: pageParam } }
      );
      return response.data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.isLastPage ? undefined : lastPageParam + 1,
    enabled: !!regionCode,
  });
};

// Add Terminal to Starlink Mutation
export const useAddTerminalToStarlink = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ regionCode, deviceId, type }: { regionCode: string, deviceId: string, type: TerminalType | null }) => {
      return await apiClient.post(
        `/enterprise/v1/user-terminals/${deviceId}`,
        {
          params: { regionCode },
          body: type ? { type } : undefined
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['added-terminal-to-starlink'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Remove Terminal from Starlink Mutation
export const useRemoveTerminalFromStarlink = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (deviceId: string) => {
      return await apiClient.delete<APIMessageResponse>(
        `/enterprise/v1/user-terminals/${deviceId}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['removed-terminal-from-starlink'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Add Terminal to Organization Mutation
export const useAddTerminalToOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ organizationId, terminalId }: { organizationId: string, terminalId: string }) => {
      return await apiClient.post<APIMessageResponse>(
        `/enterprise/v1/user-terminals/${terminalId}/organizations/${organizationId}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['added-terminal-to-account'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Remove Terminal from Organization Mutation
export const useRemoveTerminalFromOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ organizationId, terminalId }: { organizationId: string, terminalId: string }) => {
      return await apiClient.delete<APIMessageResponse>(
        `/enterprise/v1/user-terminals/${terminalId}/organizations/${organizationId}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['removed-terminal-from-account'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Update Terminal Type Mutation
export const useUpdateTerminalType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ terminalId, type }: { terminalId: string, type: TerminalType }) => {
      return await apiClient.put<APIMessageResponse>(
        `/enterprise/v1/user-terminals/${terminalId}/type`,
        {
          body: { type },
        }
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user-terminal', variables.terminalId] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};